import { __extends } from "tslib";
import { AssetEvent } from '@awayjs/core';
import { BlendMode, ImageCube } from '@awayjs/stage';
import { PartitionBase, BoundingVolumeType } from '@awayjs/view';
import { RenderableEvent, MaterialEvent, Style, ImageTextureCube, DefaultRenderer } from '@awayjs/renderer';
import { DisplayObjectContainer } from './DisplayObjectContainer';
/**
 * A Skybox class is used to render a sky in the scene. It's always considered static and 'at infinity', and as
 * such it's always centered at the camera's position and sized to exactly fit within the camera's frustum, ensuring
 * the sky box is always as large as possible without being clipped.
 */
var Skybox = /** @class */ (function (_super) {
    __extends(Skybox, _super);
    function Skybox(imageColor, alpha) {
        if (imageColor === void 0) { imageColor = 0xFFFFFF; }
        if (alpha === void 0) { alpha = 1; }
        var _this = _super.call(this) || this;
        _this._textures = new Array();
        _this._blendMode = BlendMode.NORMAL;
        _this.animateUVs = false;
        _this.bothSides = false;
        _this.curves = false;
        _this.imageRect = false;
        _this.useColorTransform = false;
        _this.alphaThreshold = 0;
        _this._onTextureInvalidateDelegate = function (event) { return _this.onTextureInvalidate(event); };
        _this._owners = [_this];
        _this.style = new Style();
        if (imageColor instanceof ImageCube) {
            _this._style.image = imageColor;
            _this.texture = new ImageTextureCube();
        }
        else {
            _this._style.color = Number(imageColor);
        }
        return _this;
    }
    Object.defineProperty(Skybox.prototype, "animationSet", {
        /**
         *
         */
        get: function () {
            return this._animationSet;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Skybox.prototype, "blendMode", {
        /**
         * The blend mode to use when drawing this renderable. The following blend modes are supported:
         * <ul>
         * <li>BlendMode.NORMAL: No blending, unless the material inherently needs it</li>
         * <li>BlendMode.LAYER: Force blending.
         * This will draw the object the same as NORMAL, but without writing depth writes.</li>
         * <li>BlendMode.MULTIPLY</li>
         * <li>BlendMode.ADD</li>
         * <li>BlendMode.ALPHA</li>
         * </ul>
         */
        get: function () {
            return this._blendMode;
        },
        set: function (value) {
            if (this._blendMode == value)
                return;
            this._blendMode = value;
            this.invalidate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Skybox.prototype, "iOwners", {
        /**
         * A list of the IRenderables that use this material
         *
         * @private
         */
        get: function () {
            return this._owners;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Skybox.prototype, "texture", {
        /**
        * The cube texture to use as the skybox.
        */
        get: function () {
            return this._texture;
        },
        set: function (value) {
            if (this._texture == value)
                return;
            if (this._texture)
                this.removeTexture(this._texture);
            this._texture = value;
            if (this._texture)
                this.addTexture(this._texture);
            this.invalidatePasses();
        },
        enumerable: true,
        configurable: true
    });
    Skybox.prototype.getNumTextures = function () {
        return this._textures.length;
    };
    Skybox.prototype.getTextureAt = function (index) {
        return this._textures[index];
    };
    Skybox.prototype.isEntity = function () {
        return true;
    };
    Object.defineProperty(Skybox.prototype, "assetType", {
        get: function () {
            return Skybox.assetType;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Marks the shader programs for all passes as invalid, so they will be recompiled before the next use.
     *
     * @private
     */
    Skybox.prototype.invalidatePasses = function () {
        this.dispatchEvent(new MaterialEvent(MaterialEvent.INVALIDATE_PASSES, this));
    };
    Skybox.prototype.invalidateElements = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_ELEMENTS, this));
    };
    Skybox.prototype.invalidateMaterial = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_MATERIAL, this));
    };
    Skybox.prototype.invalidateStyle = function () {
        this.dispatchEvent(new RenderableEvent(RenderableEvent.INVALIDATE_STYLE, this));
    };
    Skybox.prototype.addTexture = function (texture) {
        this._textures.push(texture);
        texture.addEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidateDelegate);
        this.onTextureInvalidate();
    };
    Skybox.prototype.removeTexture = function (texture) {
        this._textures.splice(this._textures.indexOf(texture), 1);
        texture.removeEventListener(AssetEvent.INVALIDATE, this._onTextureInvalidateDelegate);
        this.onTextureInvalidate();
    };
    Skybox.prototype.onTextureInvalidate = function (event) {
        if (event === void 0) { event = null; }
        this.invalidate();
    };
    Skybox.prototype._onInvalidateProperties = function (event) {
        this.invalidateMaterial();
        this.invalidatePasses();
    };
    Skybox.prototype._acceptTraverser = function (traverser) {
        traverser.applyTraversable(this);
    };
    Skybox.prototype.iAddOwner = function (owner) {
    };
    /**
     * Removes an IEntity as owner.
     * @param owner
     *
     * @internal
     */
    Skybox.prototype.iRemoveOwner = function (owner) {
    };
    Skybox.prototype._getDefaultBoundingVolume = function () {
        return BoundingVolumeType.NULL;
    };
    Skybox.prototype.testCollision = function (collision, closestFlag) {
        collision.traversable = null;
        return false;
    };
    Skybox.assetType = '[asset Skybox]';
    return Skybox;
}(DisplayObjectContainer));
export { Skybox };
import { _Render_RenderableBase, ShaderBase, RenderEntity } from '@awayjs/renderer';
import { ContextGLCompareMode, AttributesBuffer } from '@awayjs/stage';
import { _Render_MaterialPassBase } from '@awayjs/renderer';
import { SkyboxElements } from '../elements/SkyboxElements';
/**
 * _Render_SkyboxMaterial forms an abstract base class for the default shaded materials provided by Stage,
 * using material methods to define their appearance.
 */
var _Render_SkyboxMaterial = /** @class */ (function (_super) {
    __extends(_Render_SkyboxMaterial, _super);
    function _Render_SkyboxMaterial(skybox, renderElements) {
        var _this = _super.call(this, skybox, renderElements) || this;
        _this._skybox = skybox;
        _this._shader = new ShaderBase(renderElements, _this, _this, _this._stage);
        _this._texture = _this._skybox.texture.getAbstraction(_this._shader);
        _this._pAddPass(_this);
        return _this;
    }
    _Render_SkyboxMaterial.prototype.onClear = function (event) {
        _super.prototype.onClear.call(this, event);
        this._texture.onClear(new AssetEvent(AssetEvent.CLEAR, this._skybox.texture));
        this._texture = null;
        this._skybox = null;
    };
    /**
     * @inheritDoc
     */
    _Render_SkyboxMaterial.prototype._pUpdateRender = function () {
        _super.prototype._pUpdateRender.call(this);
        this.requiresBlending = (this._material.blendMode != BlendMode.NORMAL);
        this.shader.setBlendMode((this._material.blendMode == BlendMode.NORMAL && this.requiresBlending) ?
            BlendMode.LAYER : this._material.blendMode);
    };
    _Render_SkyboxMaterial.prototype._includeDependencies = function (shader) {
        _super.prototype._includeDependencies.call(this, shader);
        shader.usesPositionFragment = true;
    };
    /**
     * @inheritDoc
     */
    _Render_SkyboxMaterial.prototype._getFragmentCode = function (registerCache, sharedRegisters) {
        return this._texture._getFragmentCode(sharedRegisters.shadedTarget, registerCache, sharedRegisters, sharedRegisters.positionVarying);
    };
    _Render_SkyboxMaterial.prototype._setRenderState = function (renderable) {
        _super.prototype._setRenderState.call(this, renderable);
        this._texture._setRenderState(renderable);
    };
    /**
     * @inheritDoc
     */
    _Render_SkyboxMaterial.prototype._activate = function () {
        _super.prototype._activate.call(this);
        this._stage.context.setDepthTest(false, ContextGLCompareMode.LESS);
        this._texture.activate();
    };
    return _Render_SkyboxMaterial;
}(_Render_MaterialPassBase));
export { _Render_SkyboxMaterial };
/**
 * @class away.pool._Render_Skybox
 */
var _Render_Skybox = /** @class */ (function (_super) {
    __extends(_Render_Skybox, _super);
    function _Render_Skybox() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * //TODO
     *
     * @returns {away.base.TriangleElements}
     * @private
     */
    _Render_Skybox.prototype._getStageElements = function () {
        var elements = _Render_Skybox._elements;
        if (!elements) {
            elements = new SkyboxElements(new AttributesBuffer(11, 4));
            elements.autoDeriveNormals = false;
            elements.autoDeriveTangents = false;
            elements.setIndices(Array(0, 1, 2, 2, 3, 0, 6, 5, 4, 4, 7, 6, 2, 6, 7, 7, 3, 2, 4, 5, 1, 1, 0, 4, 4, 0, 3, 3, 7, 4, 2, 1, 5, 5, 6, 2));
            elements.setPositions(Array(-1, 1, -1, 1, 1, -1, 1, 1, 1, -1, 1, 1, -1, -1, -1, 1, -1, -1, 1, -1, 1, -1, -1, 1));
        }
        return elements.getAbstraction(this._stage);
    };
    _Render_Skybox.prototype._getRenderMaterial = function () {
        return this._asset.getAbstraction(this.renderGroup.getRenderElements(this.stageElements.elements));
    };
    _Render_Skybox.prototype._getStyle = function () {
        return this._asset.style;
    };
    return _Render_Skybox;
}(_Render_RenderableBase));
export { _Render_Skybox };
import { EntityNode } from '@awayjs/view';
// import { CacheRenderer } from '@awayjs/renderer';
/**
 * SkyboxNode is a space partitioning leaf node that contains a Skybox object.
 *
 * @class away.partition.SkyboxNode
 */
var SkyboxNode = /** @class */ (function (_super) {
    __extends(SkyboxNode, _super);
    function SkyboxNode() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     *
     * @param planes
     * @param numPlanes
     * @returns {boolean}
     */
    SkyboxNode.prototype.isInFrustum = function (rootEntity, planes, numPlanes, pickGroup) {
        if (this.isInvisible())
            return false;
        //a skybox is always in view unless its visibility is set to false
        return true;
    };
    /**
     *
     * @returns {boolean}
     */
    SkyboxNode.prototype.isCastingShadow = function () {
        return false; //skybox never casts shadows
    };
    return SkyboxNode;
}(EntityNode));
export { SkyboxNode };
//CacheRenderer.registerMaterial(_Render_SkyboxMaterial, Skybox);
DefaultRenderer.registerMaterial(_Render_SkyboxMaterial, Skybox);
RenderEntity.registerRenderable(_Render_Skybox, Skybox);
PartitionBase.registerAbstraction(SkyboxNode, Skybox);
